// Lib
import { useCallback, useEffect } from 'react';
import { TableDataVersion } from '../../../common/table/TableTypes';
import { useHotTableDispatch } from './store/hotTableStoreHooks';
import { setActiveTable } from './store/hotTableStoreActions';
import rafThrottle from '../../../common/utils/lib/rafThrottle';
import { LegacyActiveTableObject } from './store/hotTableStoreTypes';
import { MilanoteHotEvents, MNHotInstance } from './tableHotInstanceTypes';

const TRACKED_HOOKS: (keyof MilanoteHotEvents)[] = [
    'afterChange',
    'afterCreateRow',
    'afterCreateCol',
    'afterRemoveRow',
    'afterRemoveCol',
    'afterRowMove',
    'afterColumnMove',
    'afterColumnResize',
    'afterSelection',
    'afterSelectionEnd',
    'afterDeselect',
];

const getLegacyActiveTableStoreEntry = (tableInstance: MNHotInstance): LegacyActiveTableObject => ({
    instance: tableInstance,
    tableDataVersion: TableDataVersion.V1,
});

/**
 * Stores the current active legacy Handsontable instance in the HotTable store & clears it when no longer edited.
 */
export const useSyncLegacyActiveTableState = (tableInstance: MNHotInstance | null, isActive: boolean) => {
    const hotTableDispatch = useHotTableDispatch();
    const dispatchUpdateActiveTable = useCallback(
        (activeTable: LegacyActiveTableObject | null) => hotTableDispatch(setActiveTable(activeTable)),
        [hotTableDispatch],
    );

    useEffect(() => {
        if (!tableInstance) return;
        if (!isActive) return;

        dispatchUpdateActiveTable(getLegacyActiveTableStoreEntry(tableInstance));

        const throttledUpdateActiveTable = rafThrottle(() =>
            dispatchUpdateActiveTable(getLegacyActiveTableStoreEntry(tableInstance)),
        );

        TRACKED_HOOKS.forEach((hook) => tableInstance.addHook(hook, throttledUpdateActiveTable));

        return () => {
            // You shouldn't be able to immediately switch from editing one table to another, or edit
            //  two tables at once, so this should be a safe operation (i.e. won't clobber another active table).
            dispatchUpdateActiveTable(null);

            throttledUpdateActiveTable.cancel();

            TRACKED_HOOKS.forEach((hook) => tableInstance.removeHook(hook, throttledUpdateActiveTable));
        };
    }, [dispatchUpdateActiveTable, isActive, tableInstance]);
};
